import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useEyeconxStore } from "../../../../../store/eyeconxStore";
import patientService from "../../../../../services/patient.service";

const PatientCommunicationPreference = ({ patient }) => {
  const clinicId = useEyeconxStore((s) => s.user.clinicId);

  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [prefs, setPrefs] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch
  } = useForm();

  // Watch dropdown values
  const prefEmail = watch("prefEmail");
  const prefSms = watch("prefSms");
  const prefCall = watch("prefCall");

  useEffect(() => {
    loadPreferences();
  }, [patient?.id]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const res = await patientService.getPatientCommunicationPreferences(
        clinicId,
        patient.id
      );
      const data = res.data;
      setPrefs(data);

      // hydrate form
      Object.keys(data).forEach((key) => {
        setValue(key, data[key]);
      });
    } catch (e) {
      console.error("Error loading communication preferences:", e);
      toast.error("Failed to load communication preferences");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maintain unique 1, 2, 3 preference ranking
   */
  const normalizePreferences = (changedField, newValue) => {
    const values = {
      prefEmail: parseInt(getValues("prefEmail")),
      prefSms: parseInt(getValues("prefSms")),
      prefCall: parseInt(getValues("prefCall")),
    };

    // Update changed field
    values[changedField] = parseInt(newValue);

    const used = new Set();
    const order = ["prefEmail", "prefSms", "prefCall"];
    const output = {};

    // Step 1: assign chosen values, fix conflicts
    for (let field of order) {
      let val = values[field];

      while (used.has(val)) {
        val++;
      }

      output[field] = val;
      used.add(val);
    }

    // Step 2: ensure numbers are within 1–3 and unique
    const sortedValues = [...order]
      .map((f) => output[f])
      .sort((a, b) => a - b);

    const remapped = {};
    order.forEach((field, idx) => {
      remapped[field] = sortedValues[idx];
    });

    // update form
    Object.entries(remapped).forEach(([field, value]) => {
      setValue(field, value);
    });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const payload = {
        ...data,
        prefEmail: parseInt(data.prefEmail),
        prefSms: parseInt(data.prefSms),
        prefCall: parseInt(data.prefCall),
      };

      await patientService.updatePatientCommunicationPreferences(
        clinicId,
        patient.id,
        payload
      );

      setEditing(false);
      toast.success("Communication preferences updated");
      loadPreferences();
    } catch (e) {
      console.error("Error updating preferences:", e);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!prefs)
    return (
      <div className="card border-0 shadow-sm my-3">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary"></div>
        </div>
      </div>
    );

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
          <h5 className="card-title mb-0">
          <i className="bi bi-chat-left-text me-2 text-primary"></i>
          Communication Preferences
        </h5>

        {!editing ? (
          <button 
            className="btn btn-outline-primary btn-sm d-flex align-items-center" 
            onClick={() => setEditing(true)}
          >
            <i className="bi bi-pencil me-1"></i>
            Edit Preference
          </button>
        ) : (
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              setEditing(false);
              loadPreferences();
            }}
          >
            Cancel
          </button>
        )}
      </div>

      <div className="card-body mt-3">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Communication Channels Section */}
          <div className="mb-4">
           
            <div className="row g-4">
              {/* Email */}
              <div className="col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      disabled={!editing}
                      {...register("isEmail")}
                    />
                    <label className="form-check-label fw-semibold d-flex align-items-center">
                      <i className="bi bi-envelope me-2 text-primary"></i>
                      Email
                    </label>
                  </div>
                  
                  <label className="form-label small text-muted mb-2">Priority Order</label>
                  <select
                    className="form-select form-select-sm"
                    disabled={!editing}
                    {...register("prefEmail")}
                    onChange={(e) => normalizePreferences("prefEmail", e.target.value)}
                  >
                    <option value={1}>1st Priority</option>
                    <option value={2}>2nd Priority</option>
                    <option value={3}>3rd Priority</option>
                  </select>
                </div>
              </div>

              {/* SMS */}
              <div className="col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      disabled={!editing}
                      {...register("isSms")}
                    />
                    <label className="form-check-label fw-semibold d-flex align-items-center">
                      <i className="bi bi-chat-dots me-2 text-success"></i>
                      SMS
                    </label>
                  </div>
                  
                  <label className="form-label small text-muted mb-2">Priority Order</label>
                  <select
                    className="form-select form-select-sm"
                    disabled={!editing}
                    {...register("prefSms")}
                    onChange={(e) => normalizePreferences("prefSms", e.target.value)}
                  >
                    <option value={1}>1st Priority</option>
                    <option value={2}>2nd Priority</option>
                    <option value={3}>3rd Priority</option>
                  </select>
                </div>
              </div>

              {/* Phone Call */}
              <div className="col-lg-4">
                <div className="border rounded p-3 h-100">
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      disabled={!editing}
                      {...register("isCall")}
                    />
                    <label className="form-check-label fw-semibold d-flex align-items-center">
                      <i className="bi bi-telephone me-2 text-info"></i>
                      Phone Call
                    </label>
                  </div>
                  
                  <label className="form-label small text-muted mb-2">Priority Order</label>
                  <select
                    className="form-select form-select-sm"
                    disabled={!editing}
                    {...register("prefCall")}
                    onChange={(e) => normalizePreferences("prefCall", e.target.value)}
                  >
                    <option value={1}>1st Priority</option>
                    <option value={2}>2nd Priority</option>
                    <option value={3}>3rd Priority</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Notification Types Section */}
          <div className="mb-4">
            <h6 className="text-dark mb-3 pb-2 border-bottom">Notification Types</h6>
            
            <div className="row g-3">
              {[
                { field: "isReminder", label: "Appointment Reminders", icon: "bi-calendar-check" },
                { field: "isNewsletter", label: "Newsletters", icon: "bi-newspaper" },
                { field: "isArticle", label: "Articles", icon: "bi-file-text" },
                { field: "isRecall", label: "Recall Notifications", icon: "bi-arrow-repeat" },
                { field: "isGlass", label: "Glass Updates", icon: "bi-eyeglasses" },
                { field: "isLens", label: "Lens Updates", icon: "bi-circle" },
                { field: "isLensOrder", label: "Lens Orders", icon: "bi-bag" },
                { field: "isThankyou", label: "Thank You Messages", icon: "bi-hand-thumbs-up" },
                { field: "isSurvey", label: "Surveys", icon: "bi-clipboard-data" },
                { field: "isFollowup", label: "Follow-ups", icon: "bi-arrow-return-right" },
                { field: "isBirthday", label: "Birthday Wishes", icon: "bi-gift" },
              ].map(({ field, label, icon }) => (
                <div className="col-md-6 col-lg-4" key={field}>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      disabled={!editing}
                      {...register(field)}
                    />
                    <label className="form-check-label d-flex align-items-center">
                      <i className={`${icon} me-2 text-muted`}></i>
                      {label}
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {editing && (
            <div className="border-top pt-3 mt-4">
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setEditing(false);
                    loadPreferences();
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary d-flex align-items-center" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-1"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PatientCommunicationPreference;